import React, { FC, useCallback } from "react";
import Skeleton from "components/Skeleton";
import UsdtDepositForm, { useUsdtDeposit } from "components/UsdtDepositForm";
import {
  ContentWrapper,
  Heading,
} from "containers/RightPanel/RightPanel.styles";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import type { DepositDto } from "types/api/deposits";
import { OpenRightPanel } from "types/common/rightPanel";
import { getProfile } from "../../../../reduxFolder/reducers/auth";
import { openRightPanel } from "../../../../reduxFolder/reducers/rightPanel";
import { authSelectUser } from "../../../../reduxFolder/selectors/globalSelectors";
import { useFormattedDateTime } from "../../../../utils/formatDate";
import {
  toastResponseError,
  toastResponseSuccess,
} from "../../../../utils/responseMessageHelper";
import { formatUsdt, tronscanTxUrl } from "../../../../utils/usdtDeposits";
import {
  BooksBalanceLayout,
  BooksBalancePanelSection,
  ContentContainer,
} from "../../styles/BooksAddFunds.style";
import BooksBalance from "../BooksBalance/BooksBalance";
import {
  BalanceSection,
  DepositRow,
  EmptyNote,
  RecentDeposits,
  RecentTitle,
  RowAmount,
  RowDate,
  RowTx,
} from "./BooksUsdtDeposit.styles";

interface BooksUsdtDepositPanelProps {
  panelData: any;
  isOpen: boolean;
  user: any;
  getProfile: () => Promise<unknown>;
  openRightPanel: OpenRightPanel;
}

const RECENT_DEPOSITS_LIMIT = 10;

const shortTx = (txId: string) =>
  txId.length > 14 ? `${txId.slice(0, 6)}…${txId.slice(-6)}` : txId;

const RecentDeposit: FC<{ item: DepositDto }> = ({ item }) => {
  const { t } = useTranslation("common");
  const date = useFormattedDateTime({
    date: item.confirmed_at ?? item.created_at,
  });

  return (
    <DepositRow>
      <RowAmount>
        {formatUsdt(parseFloat(item.amount))} {item.currency}
      </RowAmount>
      <RowDate>{date}</RowDate>
      {item.tx_id ? (
        <RowTx
          href={tronscanTxUrl(item.tx_id)}
          target="_blank"
          rel="noopener noreferrer"
          title={t("usdtDeposit.viewTx")}
        >
          {shortTx(item.tx_id)}
        </RowTx>
      ) : (
        <span />
      )}
    </DepositRow>
  );
};

const BooksUsdtDepositPanel: FC<BooksUsdtDepositPanelProps> = React.memo(
  ({ panelData, isOpen, user, getProfile, openRightPanel }) => {
    const { t } = useTranslation("common");

    // Every transfer to the address is credited on its own; the balance is
    // reloaded once per poll that brought new ones.
    const handleNewDeposits = useCallback(
      (fresh: DepositDto[]) => {
        fresh.forEach((deposit) =>
          toastResponseSuccess(
            t("books.usdt.successfulTransactions", {
              amount: formatUsdt(parseFloat(deposit.amount)),
            }),
          ),
        );
        getProfile().catch(toastResponseError);
      },
      [getProfile, t],
    );

    const deposit = useUsdtDeposit({
      isOpen,
      onNewDeposits: handleNewDeposits,
    });
    const { deposits } = deposit;

    const helpPanelContext = {
      openRightPanel,
      returnPanelType: "BOOKS_USDT_DEPOSIT",
      getReturnPanelData: () => panelData,
    };

    return (
      <ContentWrapper>
        <Heading $isBordered>{t("books.usdt.panelTitle")}</Heading>
        <ContentContainer>
          <BalanceSection>
            <BooksBalancePanelSection>
              <BooksBalanceLayout>
                <BooksBalance balance={user.get("balance")} />
              </BooksBalanceLayout>
            </BooksBalancePanelSection>
          </BalanceSection>
          <UsdtDepositForm
            deposit={deposit}
            helpPanelContext={helpPanelContext}
          />
          <RecentDeposits>
            <RecentTitle>{t("books.usdt.recentDeposits")}</RecentTitle>
            {deposits === null ? (
              <Skeleton height="48px" borderRadius="4px" />
            ) : deposits.length === 0 ? (
              <EmptyNote>{t("books.usdt.noDeposits")}</EmptyNote>
            ) : (
              deposits
                .slice(0, RECENT_DEPOSITS_LIMIT)
                .map((item) => <RecentDeposit key={item.id} item={item} />)
            )}
          </RecentDeposits>
        </ContentContainer>
      </ContentWrapper>
    );
  },
);

const withConnect = connect(
  createStructuredSelector({
    user: authSelectUser,
  }),
  {
    getProfile,
    openRightPanel,
  },
);

export default withConnect(BooksUsdtDepositPanel);
